import { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { JobPostFormInput } from "../types";

interface MissingField {
  label: string;
  path: string;
}

interface ValidationState {
  isDraftValid: boolean;
  isPublishValid: boolean;
  draftMissingFields: MissingField[];
  publishMissingFields: MissingField[];
}

export function useFormValidation(
  form: UseFormReturn<JobPostFormInput>,
  getSchema: (serviceLevel: string, submitAction: string, variant: "draft" | "primary") => any,
  serviceLevel: string,
  submitAction: string
): ValidationState {
  const formData = form.watch();

  return useMemo(() => {
    const draftSchema = getSchema(serviceLevel, submitAction, "draft");
    const primarySchema = getSchema(serviceLevel, submitAction, "primary");

    const draftResult = draftSchema.safeParse(formData);
    const primaryResult = primarySchema.safeParse(formData);

    const segmentMap: Record<string, string> = {
      compensation: "Compensation",
      pointOfContact: "Contact"
    };

    const prettifySegment = (segment: string) => {
      const mapped = segmentMap[segment];
      if (mapped) return mapped;
      const humanizedString = segment.replace(/([A-Z])/g, " $1").replace(/^./, (str: string) => str.toUpperCase());
      // Filter out "id" (case-insensitive) from the humanizedString string
      return humanizedString.replace(/\bid\b/gi, "").trim();
    };

    const extractMissingFields = (validationResult: any, isDraft: boolean = false): MissingField[] => {
      if (validationResult.success) return [];

      const issues = validationResult.error.issues || [];
      const seen = new Set<string>();

      const shouldFilterServiceLevel = serviceLevel === "Self-Serve" && (isDraft || submitAction === "Save & Publish");

      const filteredIssues = shouldFilterServiceLevel
        ? issues.filter((issue: any) => {
            const pathArray = issue.path as string[];
            return pathArray.length === 0 || pathArray[0] !== "serviceLevel";
          })
        : issues;

      const missingFields = filteredIssues.reduce((uniqueFields: MissingField[], issue: any) => {
        const pathArray = issue.path as string[];

        if (pathArray.length === 0) {
          if (!seen.has("Unknown field")) {
            seen.add("Unknown field");
            uniqueFields.push({ label: "Unknown field", path: "" });
          }
          return uniqueFields;
        }

        // Convert each path segment to a friendly label
        const friendlySegments = pathArray.map(prettifySegment);

        // If nested, join with space to read naturally (e.g., "Compensation Base Minimum")
        const fieldLabel = friendlySegments.join(" ");
        const pathString = pathArray.join(".");

        if (!seen.has(pathString)) {
          seen.add(pathString);
          uniqueFields.push({ label: fieldLabel, path: pathString });
        }

        return uniqueFields;
      }, [] as MissingField[]);

      // Add conditional missing fields based on form state
      const applyVia = formData.applyVia;
      const externalJobUrl = formData.externalJobUrl;

      // If applyVia is "Facility Website" and externalJobUrl is missing, add it to missing fields
      if (applyVia === "Facility Website" && (!externalJobUrl || externalJobUrl.trim() === "")) {
        const externalJobUrlPath = "externalJobUrl";
        if (!seen.has(externalJobUrlPath)) {
          seen.add(externalJobUrlPath);
          missingFields.push({ label: "Application URL", path: externalJobUrlPath });
        }
      }

      return missingFields;
    };

    const draftMissingFields = extractMissingFields(draftResult, true);
    const publishMissingFields = extractMissingFields(primaryResult, false);

    return {
      isDraftValid: draftResult.success,
      isPublishValid: primaryResult.success,
      draftMissingFields,
      publishMissingFields
    };
  }, [formData, getSchema, serviceLevel, submitAction]);
}
